import { useQuery, QueryResult } from '@apollo/client';
import { OptionalBoolean, OptionalString, parseQueryString, updateQueryString } from '@sixfold/app-utils';
import { Stack } from '@sixfold/common-ui';
import { Children, renderChildren } from '@sixfold/typed-render-props';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';

import { InfiniteScroll } from '../../components/infinitescroll';
import {
  CompanyAgreementStatus,
  CompanyListQuery,
  CompanyListQueryVariables,
  getNodes,
  loadMoreFromConnection,
  PAGINATION_PAGE_SIZE,
  UnifiedCompanySignupStatus,
  CompaniesFilterOptionsQuery,
  CompaniesFilterOptionsQueryVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { useDebounce } from '../../lib/util/debounce';
import { CompanyFilterInput, CompanyFilters, SIGNUP_STATUS_REVERTED_VALUE } from '../components/company_filters';
import { CompanyList, CompanyListDataProps } from '../components/company_list';
import { companiesQuery, companiesFilterOptionsQuery } from '../graphql';
import { filterBackofficeCompanyFeatureFlagMetadata } from '../vehicle_history_feature_flags';

class CompaniesListLoadingContainer extends Loader<CompanyListQuery, CompanyListDataProps, object> {}

const CompaniesList: React.FC<{
  activeFilters: {
    textSearch: string | undefined;
    tagName: string | undefined;
    type: {
      is_carrier: boolean | undefined;
      is_shipper: boolean | undefined;
      is_test: boolean | undefined;
      is_paying_customer: boolean | undefined;
      is_pilot: boolean | undefined;
    };
    signupStatus: string | undefined;
    featureFlags: string[] | undefined;
    agreementStatuses: string[] | undefined;
  };
  children: Children<
    CompanyListDataProps & {
      result: QueryResult<CompanyListQuery, CompanyListQueryVariables>;
    }
  >;
  onFilterToggle: (filter: { name: keyof CompanyFilterInput; value: string | string[] | undefined }) => void;
  onClearFilters: () => void;
}> = ({ activeFilters, children, onFilterToggle, onClearFilters }) => {
  const debouncedSearchCompanyName = useDebounce(activeFilters.textSearch);

  const isRevertedFilter = activeFilters.signupStatus === SIGNUP_STATUS_REVERTED_VALUE;
  const signupStatusForQuery = isRevertedFilter
    ? UnifiedCompanySignupStatus.NO_VISIBILITY_SOURCE
    : (activeFilters.signupStatus?.toUpperCase() as UnifiedCompanySignupStatus | undefined);

  const companiesQueryResult = useQuery<CompanyListQuery, CompanyListQueryVariables>(companiesQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      limit: PAGINATION_PAGE_SIZE,
      type: activeFilters.type,
      textSearch: debouncedSearchCompanyName,
      tagName: activeFilters.tagName,
      signupStatus: signupStatusForQuery,
      featureFlags: activeFilters.featureFlags,
      showOnlyOnboardingRolledBack: isRevertedFilter ? true : undefined,
      agreementStatuses: activeFilters.agreementStatuses as CompanyAgreementStatus[] | undefined,
    },
  });

  const companiesFilterOptionsResult = useQuery<CompaniesFilterOptionsQuery, CompaniesFilterOptionsQueryVariables>(
    companiesFilterOptionsQuery,
  );

  return (
    <Stack direction="column" gap="medium" style={{ height: 'calc(100vh - 100px)' }}>
      <CompanyFilters
        availableTags={companiesFilterOptionsResult.data?.availableCompanyTags || []}
        availableFeatureFlags={filterBackofficeCompanyFeatureFlagMetadata(
          companiesFilterOptionsResult.data?.featureFlagMetadata ?? [],
        )}
        activeFilters={activeFilters}
        onClearFilters={onClearFilters}
        onFilterToggle={onFilterToggle}
      />
      <CompaniesListLoadingContainer
        result={companiesQueryResult}
        mapData={({ data }) => ({
          companies: getNodes(data.companies),
        })}
        options={{
          showLoadingPlaceholder: ({ networkStatus, loading }) => {
            const isFetchingMore = networkStatus === 3;
            return isFetchingMore ? false : loading;
          },
        }}>
        {({ companies }) => renderChildren(children as React.ReactNode, { companies, result: companiesQueryResult })}
      </CompaniesListLoadingContainer>
    </Stack>
  );
};

export const CompanyListContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    text_search: textSearch,
    tag_name: tagName,
    signup_status: signupStatus,
    feature_flags: featureFlags,
    agreement_status: agreementStatus,
    ...type
  } = parseQueryString({
    queryString: location.search,
    queryParametersToParse: {
      is_carrier: OptionalBoolean,
      is_shipper: OptionalBoolean,
      is_test: OptionalBoolean,
      is_paying_customer: OptionalBoolean,
      is_pilot: OptionalBoolean,
      is_pending_signup: OptionalBoolean,
      text_search: OptionalString,
      tag_name: OptionalString,
      signup_status: OptionalString,
      feature_flags: OptionalString,
      agreement_status: OptionalString,
    },
  });

  return (
    <CompaniesList
      activeFilters={{
        textSearch,
        tagName,
        type,
        signupStatus,
        featureFlags: featureFlags?.split(','),
        agreementStatuses: agreementStatus?.split(','),
      }}
      onClearFilters={() => void navigate(location.pathname)}
      onFilterToggle={({ name, value }) =>
        void navigate(
          `${location.pathname}?${updateQueryString(location.search, {
            param: name,
            value: value === undefined ? undefined : [value].flat().join(','),
          })}`,
        )
      }>
      {({ companies, result: companiesResult }) => (
        <InfiniteScroll
          className="company__company-list"
          triggerOnWindow={false}
          loadMoreEntries={() => loadMoreFromConnection(companiesQuery, companiesResult, ['companies'])}>
          <CompanyList companies={companies} />
        </InfiniteScroll>
      )}
    </CompaniesList>
  );
};
